import React, { useState, useEffect, useRef } from 'react';
import '../styles/FocusTimer.css';
import { Slider } from 'primereact/slider';
import "primereact/resources/themes/lara-light-indigo/theme.css";
import firebase from '../firebaseConfig';
import { FiSettings } from 'react-icons/fi';

const FocusTimer = ({ user }) => {
    // Durées prédéfinies en minutes
    const presetDurations = {
        focus: { min: 30, max: 120, default: 45 }, // 30min - 2h
        shortBreak: { min: 1, max: 20, default: 10 }, // 5-20 min
        longBreak: { min: 30, max: 90, default: 45 } // 30min - 1h30
    };
    
    // État pour la modale de configuration
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [tempSettings, setTempSettings] = useState(null);
    const [isUpdatingSettings, setIsUpdatingSettings] = useState(false);

    const [mode, setMode] = useState('focus'); // 'focus', 'shortBreak', 'longBreak'
    const [customDuration, setCustomDuration] = useState({
        focus: presetDurations.focus.default,
        shortBreak: presetDurations.shortBreak.default,
        longBreak: presetDurations.longBreak.default
    });
    const [sessions, setSessions] = useState(0);
    const [lastSessionDate, setLastSessionDate] = useState(null);
    const [showNotification, setShowNotification] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState('');
    const [completedFocus, setCompletedFocus] = useState(false); // Pour suivre si un cycle focus a été complété
    
    // Fonction pour obtenir la date actuelle au format YYYY-MM-DD
    const getCurrentDateString = () => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    };
    
    // Log pour déboguer l'objet utilisateur
    useEffect(() => {
        console.log("Objet utilisateur:", user);
    }, [user]);
    
    // Charger les durées et les sessions sauvegardées depuis Firebase
    useEffect(() => {
        const loadSavedData = async () => {
            if (user) {
                try {
                    const userId = user.uid;
                    const docRef = firebase.firestore().collection('users').doc(userId);
                    const docSnapshot = await docRef.get();
                    
                    if (docSnapshot.exists) {
                        const userData = docSnapshot.data();
                        
                        // Charger les paramètres du timer
                        if (userData.timerSettings) {
                            setCustomDuration(userData.timerSettings);
                        }
                        
                        // Charger le compteur de sessions
                        if (userData.timerSessions) {
                            console.log("Sessions trouvées dans Firebase:", userData.timerSessions);
                            const currentDate = getCurrentDateString();
                            console.log("Date actuelle:", currentDate);
                            
                            // Vérifier si les sessions sont de la journée actuelle
                            if (userData.timerSessions.date === currentDate) {
                                console.log("Sessions de la journée actuelle:", userData.timerSessions.count);
                                // Utiliser les sessions de la journée actuelle
                                setSessions(userData.timerSessions.count);
                                setLastSessionDate(userData.timerSessions.date);
                            } else {
                                // Réinitialiser le compteur pour une nouvelle journée
                                setSessions(0);
                                setLastSessionDate(currentDate);
                                
                                // Mettre à jour Firebase avec le nouveau compteur à 0
                                await docRef.set({
                                    timerSessions: {
                                        count: 0,
                                        date: currentDate,
                                        lastUpdated: firebase.firestore.Timestamp.fromDate(new Date())
                                    }
                                }, { merge: true });
                            }
                        } else {
                            // Initialiser le compteur de sessions s'il n'existe pas
                            const currentDate = getCurrentDateString();
                            setSessions(0);
                            setLastSessionDate(currentDate);
                            
                            // Créer l'objet timerSessions dans Firebase
                            await docRef.set({
                                timerSessions: {
                                    count: 0,
                                    date: currentDate,
                                    lastUpdated: firebase.firestore.Timestamp.fromDate(new Date())
                                }
                            }, { merge: true });
                        }
                    }
                } catch (error) {
                    console.error("Erreur lors du chargement des données du timer:", error);
                }
            }
        };
        
        loadSavedData();
    }, [user]);
    const [time, setTime] = useState(customDuration.focus * 60); // Durée en secondes
    const [isActive, setIsActive] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const intervalRef = useRef(null);
    const audioRef = useRef(null);

    const modes = {
        focus: { 
            duration: customDuration.focus * 60, 
            label: 'Focus Time', 
            color: '#4066ff',
            min: presetDurations.focus.min,
            max: presetDurations.focus.max
        },
        shortBreak: { 
            duration: customDuration.shortBreak * 60, 
            label: 'Short Break', 
            color: '#28a745',
            min: presetDurations.shortBreak.min,
            max: presetDurations.shortBreak.max
        },
        longBreak: { 
            duration: customDuration.longBreak * 60, 
            label: 'Long Break', 
            color: '#ffc107',
            min: presetDurations.longBreak.min,
            max: presetDurations.longBreak.max
        }
    };

    // Fonction pour jouer le son de notification
    const playNotificationSound = () => {
        try {
            // Créer un contexte audio
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            
            // Créer un oscillateur pour le son principal
            const oscillator1 = audioContext.createOscillator();
            const gainNode1 = audioContext.createGain();
            
            oscillator1.connect(gainNode1);
            gainNode1.connect(audioContext.destination);
            
            // Configurer le premier son (plus aigu)
            oscillator1.frequency.setValueAtTime(880, audioContext.currentTime); // La4 (880 Hz)
            gainNode1.gain.setValueAtTime(0, audioContext.currentTime);
            gainNode1.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.05);
            gainNode1.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.5);
            
            // Créer un deuxième oscillateur pour un son plus grave
            const oscillator2 = audioContext.createOscillator();
            const gainNode2 = audioContext.createGain();
            
            oscillator2.connect(gainNode2);
            gainNode2.connect(audioContext.destination);
            
            // Configurer le deuxième son (plus grave)
            oscillator2.frequency.setValueAtTime(587.33, audioContext.currentTime + 0.6); // Ré4 (587.33 Hz)
            gainNode2.gain.setValueAtTime(0, audioContext.currentTime + 0.6);
            gainNode2.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.65);
            gainNode2.gain.linearRampToValueAtTime(0, audioContext.currentTime + 1.1);
            
            // Jouer les sons
            oscillator1.start(audioContext.currentTime);
            oscillator1.stop(audioContext.currentTime + 0.6);
            
            oscillator2.start(audioContext.currentTime + 0.6);
            oscillator2.stop(audioContext.currentTime + 1.2);
            
            console.log('Timer notification sound played');
        } catch (error) {
            console.error('Error playing notification sound:', error);
        }
    };
    
    // Fonction pour afficher une notification visuelle dans l'interface
    const showVisualNotification = (message) => {
        setNotificationMessage(message);
        setShowNotification(true);
        
        // Masquer la notification après 5 secondes
        setTimeout(() => {
            setShowNotification(false);
        }, 5000);
    };
    
    // Fonction pour demander la permission pour les notifications
    const requestNotificationPermission = async () => {
        if (!('Notification' in window)) {
            console.log('Ce navigateur ne prend pas en charge les notifications');
            return false;
        }
        
        if (Notification.permission === 'granted') {
            return true;
        }
        
        if (Notification.permission !== 'denied') {
            const permission = await Notification.requestPermission();
            return permission === 'granted';
        }
        
        return false;
    };
    
    // Demander la permission pour les notifications au chargement du composant
    useEffect(() => {
        requestNotificationPermission();
    }, []);
    
    // Fonction pour afficher une notification du navigateur/Windows
    const showBrowserNotification = (title, body) => {
        // Vérifier si les notifications sont supportées
        if (!('Notification' in window)) {
            console.log('Ce navigateur ne prend pas en charge les notifications');
            return;
        }
        
        // Vérifier si l'autorisation est déjà accordée
        if (Notification.permission === 'granted') {
            try {
                // Jouer un son de notification Windows
                const audio = new Audio('/notification-sound.mp3');
                audio.play().catch(error => console.error('Erreur lors de la lecture du son:', error));
                
                // Créer la notification
                const notification = new Notification(title, {
                    body: body,
                    icon: '/favicon.ico',
                    image: '/timer-complete.png', // Image optionnelle pour les notifications riches
                    vibrate: [200, 100, 200, 100, 200], // Motif de vibration plus distinctif
                    badge: '/favicon.ico',
                    tag: 'timer-notification',
                    renotify: true,
                    silent: false, // Utiliser le son du système
                    requireInteraction: true, // La notification reste jusqu'à ce que l'utilisateur interagisse avec
                    actions: [ // Actions pour les notifications riches (supporté sur certains navigateurs)
                        {
                            action: 'restart',
                            title: 'Restart Timer'
                        },
                        {
                            action: 'dismiss',
                            title: 'Dismiss'
                        }
                    ]
                });
                
                // Gestionnaire pour les actions de notification
                notification.onaction = (event) => {
                    if (event.action === 'restart') {
                        resetTimer();
                        setIsActive(true);
                    }
                    notification.close();
                };
                
                // Fermer automatiquement la notification après 10 secondes
                setTimeout(() => notification.close(), 10000);
                
                // Ajouter un gestionnaire d'événements pour le clic sur la notification
                notification.onclick = () => {
                    window.focus();
                    notification.close();
                };
            } catch (error) {
                console.error('Erreur lors de l\'affichage de la notification:', error);
                
                // Fallback pour les navigateurs qui ne supportent pas certaines fonctionnalités
                try {
                    const simpleNotification = new Notification(title, {
                        body: body,
                        icon: '/favicon.ico'
                    });
                    setTimeout(() => simpleNotification.close(), 5000);
                } catch (fallbackError) {
                    console.error('Erreur lors du fallback de notification:', fallbackError);
                }
            }
        } else if (Notification.permission !== 'denied') {
            // Demander l'autorisation
            Notification.requestPermission().then(permission => {
                if (permission === 'granted') {
                    showBrowserNotification(title, body);
                }
            });
        }
    };

    // Log pour déboguer le mode et le temps restant
    useEffect(() => {
        if (isActive) {
            console.log(`Timer actif: mode=${mode}, temps restant=${time}s, isPaused=${isPaused}`);
        }
    }, [isActive, isPaused, mode, time]);

    useEffect(() => {
        if (isActive && !isPaused) {
            console.log("Démarrage du timer:", mode);
            intervalRef.current = setInterval(() => {
                setTime(time => {
                    if (time <= 1) {
                        console.log("Timer terminé, mode actuel:", mode);
                        // Timer terminé
                        setIsActive(false);
                        setIsPaused(false);
                        
                        // Jouer le son de notification
                        playNotificationSound();
                        
                        // Message de notification
                        const notifMessage = `${modes[mode].label} completed!`;
                        
                        // Afficher la notification visuelle dans l'interface
                        showVisualNotification(notifMessage);
                        
                        // Afficher la notification du navigateur
                        showBrowserNotification('Focus Timer', notifMessage);

                        // Auto-switch modes
                        if (mode === 'focus') {
                            console.log("Mode focus terminé, marquage comme complété");
                            // Marquer le focus comme complété
                            setCompletedFocus(true);
                            
                            // Incrémenter le compteur de sessions
                            console.log("Sessions avant incrémentation:", sessions);
                            // Utiliser une fonction de mise à jour pour garantir la valeur la plus récente
                            setSessions(prevSessions => {
                                const newSessionCount = prevSessions + 1;
                                console.log("Nouveau compteur calculé:", newSessionCount);
                                
                                // Sauvegarder le nouveau compteur dans Firebase
                                if (user) {
                                    const userId = user.uid;
                                    const docRef = firebase.firestore().collection('users').doc(userId);
                                    const currentDate = getCurrentDateString();
                                    const now = new Date();
                                    
                                    console.log("Sauvegarde du compteur dans Firebase:", newSessionCount);
                                    
                                    // Utiliser set avec merge pour éviter les problèmes de mise à jour
                                    docRef.set({
                                        timerSessions: {
                                            count: newSessionCount,
                                            date: currentDate,
                                            lastUpdated: firebase.firestore.Timestamp.fromDate(now)
                                        }
                                    }, { merge: true })
                                    .then(() => {
                                        console.log("Compteur sauvegardé avec succès:", newSessionCount);
                                        setLastSessionDate(currentDate);
                                    })
                                    .catch(error => {
                                        console.error("Erreur lors de la sauvegarde du compteur de sessions:", error);
                                    });
                                }
                                
                                return newSessionCount;
                            });
                            
                            // Déterminer le prochain mode
                            const nextMode = sessions % 4 === 3 ? 'longBreak' : 'shortBreak';
                            console.log("Passage au mode suivant:", nextMode);
                            setMode(nextMode);
                            setTime(modes[nextMode].duration);
                        } else if (mode === 'shortBreak' || mode === 'longBreak') {
                            // Après une pause, revenir au mode focus
                            console.log("Pause terminée, retour au mode focus");
                            setMode('focus');
                            setTime(modes.focus.duration);
                        }
                        
                        return 0;
                    }
                    return time - 1;
                });
            }, 1000);
        } else {
            clearInterval(intervalRef.current);
        }

        return () => clearInterval(intervalRef.current);
    }, [isActive, isPaused, mode, sessions]);
    
    // Mettre à jour les durées lorsque customDuration change
    useEffect(() => {
        if (!isActive) {
            setTime(customDuration[mode] * 60);
        }
    }, [customDuration, mode]);

    const toggleTimer = () => {
        if (isActive) {
            console.log("Pause/Reprise du timer, isPaused:", !isPaused);
            setIsPaused(!isPaused);
        } else {
            console.log("Démarrage du timer, mode:", mode);
            setIsActive(true);
            setIsPaused(false);
        }
    };

    const resetTimer = () => {
        console.log("Réinitialisation du timer, mode:", mode);
        setIsActive(false);
        setIsPaused(false);
        setCompletedFocus(false);
        setTime(modes[mode].duration);
    };

    const switchMode = (newMode) => {
        console.log("Changement de mode:", mode, "=>", newMode);
        setMode(newMode);
        setTime(modes[newMode].duration);
        setIsActive(false);
        setIsPaused(false);
        setCompletedFocus(false);
    };
    
    const handleDurationChange = (value, currentMode) => {
        const newDurations = {
            ...customDuration,
            [currentMode]: value
        };
        
        setCustomDuration(newDurations);
        
        if (mode === currentMode && !isActive) {
            setTime(value * 60);
        }
        
        // Sauvegarder les durées dans Firebase
        saveTimerSettings(newDurations);
    };
    
    // Fonction pour sauvegarder les paramètres du timer dans Firebase
    const saveTimerSettings = async (settings) => {
        if (user) {
            try {
                const userId = firebase.auth().currentUser.uid;
                const docRef = firebase.firestore().collection('users').doc(userId);
                await docRef.update({ timerSettings: settings });
                return true;
            } catch (error) {
                console.error("Erreur lors de la sauvegarde des durées de timer:", error);
                return false;
            }
        }
        return false;
    };
    
    // Ouvrir la modale de configuration
    const openSettingsModal = () => {
        setTempSettings({...customDuration});
        setShowSettingsModal(true);
    };
    
    // Fermer la modale de configuration
    const closeSettingsModal = () => {
        setShowSettingsModal(false);
        setTempSettings(null);
    };
    
    // Sauvegarder les paramètres depuis la modale
    const saveSettingsFromModal = async () => {
        setIsUpdatingSettings(true);
        const success = await saveTimerSettings(tempSettings);
        
        if (success) {
            setCustomDuration(tempSettings);
            if (mode === 'focus') setTime(tempSettings.focus * 60);
            if (mode === 'shortBreak') setTime(tempSettings.shortBreak * 60);
            if (mode === 'longBreak') setTime(tempSettings.longBreak * 60);
            closeSettingsModal();
        }
        
        setIsUpdatingSettings(false);
    };
    
    // Gérer les changements dans la modale
    const handleTempSettingChange = (value, settingKey) => {
        setTempSettings(prev => ({
            ...prev,
            [settingKey]: value
        }));
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const getProgress = () => {
        return ((modes[mode].duration - time) / modes[mode].duration) * 100;
    };

    // Demander permission pour les notifications
    useEffect(() => {
        if ('Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission();
        }
    }, []);

    return (
        <div className="focus-timer-card">
            <div className="timer-header">
                <h3>
                    <i className="fas fa-clock"></i>
                    Focus Timer
                </h3>
                <div className="timer-actions">
                    <button 
                        className="settings-button" 
                        onClick={openSettingsModal}
                        title="Timer Settings"
                    >
                        <FiSettings />
                    </button>
                </div>
            </div>

            <div className="timer-modes">
                {Object.entries(modes).map(([key, modeData]) => (
                    <button
                        key={key}
                        className={`mode-btn ${mode === key ? 'active' : ''}`}
                        onClick={() => switchMode(key)}
                        style={{ '--mode-color': modeData.color }}
                        disabled={isActive && !isPaused}
                    >
                        {modeData.label}
                    </button>
                ))}
            </div>

            <div className="timer-display">
                <div 
                    className="timer-circle"
                    style={{ '--progress': getProgress(), '--mode-color': modes[mode].color }}
                >
                    <div className="timer-time">
                        {formatTime(time)}
                    </div>
                    <div className="timer-label">
                        {modes[mode].label}
                    </div>
                </div>
            </div>

            <div className="timer-controls">
                <button
                    className={`control-btn primary ${isActive ? 'pause' : 'play'}`}
                    onClick={toggleTimer}
                >
                    <i className={`fas ${isActive && !isPaused ? 'fa-pause' : 'fa-play'}`}></i>
                    {isActive && !isPaused ? 'Pause' : 'Start'}
                </button>
                
                <button
                    className="control-btn secondary"
                    onClick={resetTimer}
                >
                    <i className="fas fa-redo"></i>
                    Reset
                </button>
            </div>

            
            
            {/* Modale de configuration des durées */}
            {showSettingsModal && (
                <div className="modal-overlay">
                    <div className="settings-modal">
                        <div className="settings-modal-header">
                            <h3>Timer Settings</h3>
                            <button 
                                className="close-button" 
                                onClick={closeSettingsModal}
                            >
                                &times;
                            </button>
                        </div>
                        
                        <div className="settings-modal-body">
                            <div className="setting-item">
                                <div className="setting-header">
                                    <label>Focus Timer: {tempSettings.focus} minutes</label>
                                </div>
                                <Slider 
                                    value={tempSettings.focus} 
                                    onChange={(e) => handleTempSettingChange(e.value, 'focus')} 
                                    min={presetDurations.focus.min} 
                                    max={presetDurations.focus.max}
                                />
                                <div className="setting-range">
                                    <span>{presetDurations.focus.min} min</span>
                                    <span>{presetDurations.focus.max} min</span>
                                </div>
                            </div>
                            
                            <div className="setting-item">
                                <div className="setting-header">
                                    <label>Short Break: {tempSettings.shortBreak} minutes</label>
                                </div>
                                <Slider 
                                    value={tempSettings.shortBreak} 
                                    onChange={(e) => handleTempSettingChange(e.value, 'shortBreak')} 
                                    min={presetDurations.shortBreak.min} 
                                    max={presetDurations.shortBreak.max}
                                />
                                <div className="setting-range">
                                    <span>{presetDurations.shortBreak.min} min</span>
                                    <span>{presetDurations.shortBreak.max} min</span>
                                </div>
                            </div>
                            
                            <div className="setting-item">
                                <div className="setting-header">
                                    <label>Long Break: {tempSettings.longBreak} minutes</label>
                                </div>
                                <Slider 
                                    value={tempSettings.longBreak} 
                                    onChange={(e) => handleTempSettingChange(e.value, 'longBreak')} 
                                    min={presetDurations.longBreak.min} 
                                    max={presetDurations.longBreak.max}
                                />
                                <div className="setting-range">
                                    <span>{presetDurations.longBreak.min} min</span>
                                    <span>{presetDurations.longBreak.max} min</span>
                                </div>
                            </div>
                        </div>
                        
                        <div className="settings-modal-footer">
                            <button 
                                className="cancel-button" 
                                onClick={closeSettingsModal}
                            >
                                Cancel
                            </button>
                            <button 
                                className="save-button" 
                                onClick={saveSettingsFromModal}
                                disabled={isUpdatingSettings}
                            >
                                {isUpdatingSettings ? 'Saving...' : 'Save Settings'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Notification visuelle */}
            {showNotification && (
                <div className="timer-notification">
                    <div className="notification-content">
                        <i className="fas fa-bell notification-icon"></i>
                        <span>{notificationMessage}</span>
                        <button 
                            className="notification-close" 
                            onClick={() => setShowNotification(false)}
                        >
                            &times;
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FocusTimer;
